'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// ============================================================================
// BIRTHDAY CONFIG — tweak these to personalize!
// ============================================================================
const BIRTHDAY = {
  name: 'Odin',
  // Drop a photo at: frontend/public/odin.jpg  (jpg or png works)
  photo: '/odin.jpg',
  photoFallback: '/odin-placeholder.svg',
}

// Funny birthday messages that cycle
const FUNNY_MESSAGES = [
  "Happy Birthday, Odin! 🎉 You're not old, you're a limited edition vintage legend.",
  "Odin, another year wiser?? Debatable. Another year more awesome?? Absolutely. 😎",
  "Warning ⚠️: Odin is now officially too cool to handle. Approach with confetti.",
  "They say age is just a number. In your case, Odin, it's a REALLY good number. 🥳",
  "Roses are red, cake is the best, today it's YOUR day, Odin, forget all the rest! 🌹🍰",
  "Odin, you're like a fine cheese — getting better, slightly cheesier, and loved by all. 🧀",
  "Breaking news 📰: Local legend named Odin levels up today. Fireworks ensue!",
]

// Surprises revealed by the big button
const SURPRISES = [
  "🎁 SURPRISE! You just unlocked +100 birthday XP. Use it wisely (mostly on cake).",
  "🎈 PLOT TWIST: The real gift was the confetti we launched along the way!",
  "🕺 DANCE BREAK! Legend says if Odin does one spin, the whole day gets luckier.",
  "🍕 Prophecy fulfilled: today, calories don't count. It's a birthday, we don't make the rules.",
  "🦸 You've been promoted to Official Birthday Superhero. Cape optional, snacks mandatory.",
  "🌟 A wish has been reserved just for you. Close your eyes, make it EPIC.",
]

// ============================================================================
// FLOATING HEARTS (background)
// ============================================================================
function FloatingHearts() {
  // Render only after mount so the random positions never cause an SSR
  // hydration mismatch (server & client would otherwise disagree).
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const hearts = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 16 + Math.random() * 28,
        duration: 8 + Math.random() * 10,
        delay: Math.random() * 8,
        emoji: ['💖', '💛', '💙', '💚', '🧡', '💜', '❤️'][i % 7],
      })),
    []
  )

  if (!mounted) return null

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
      {hearts.map((h) => (
        <motion.span
          key={h.id}
          className="absolute select-none"
          style={{ left: `${h.left}%`, fontSize: h.size, bottom: -60 }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: '-110vh', opacity: [0, 1, 1, 0], x: [0, 20, -20, 0] }}
          transition={{
            duration: h.duration,
            delay: h.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {h.emoji}
        </motion.span>
      ))}
    </div>
  )
}

// ============================================================================
// CONFETTI BURST (on demand)
// ============================================================================
type ConfettiPiece = {
  id: number
  x: number
  color: string
  rotate: number
  duration: number
  delay: number
  size: number
  drift: number
}

function ConfettiBurst({ fireKey }: { fireKey: number }) {
  const colors = ['#f472b6', '#a78bfa', '#38bdf8', '#facc15', '#34d399', '#fb923c', '#f87171']
  const pieces = useMemo<ConfettiPiece[]>(() => {
    if (fireKey === 0) return []
    return Array.from({ length: 120 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: colors[i % colors.length],
      rotate: Math.random() * 720 - 360,
      duration: 2.2 + Math.random() * 2.2,
      delay: Math.random() * 0.4,
      size: 6 + Math.random() * 10,
      drift: Math.random() * 200 - 100,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireKey])

  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden" aria-hidden="true">
      <AnimatePresence>
        {pieces.map((p) => (
          <motion.span
            key={`${fireKey}-${p.id}`}
            className="absolute rounded-sm"
            style={{
              left: `${p.x}%`,
              top: -20,
              width: p.size,
              height: p.size * 1.4,
              backgroundColor: p.color,
            }}
            initial={{ y: -20, opacity: 1, rotate: 0 }}
            animate={{ y: '110vh', x: p.drift, rotate: p.rotate, opacity: [1, 1, 0.9, 0] }}
            transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function OdinBirthdayPage() {
  const [messageIndex, setMessageIndex] = useState(0)
  const [confettiKey, setConfettiKey] = useState(0)
  const [surprise, setSurprise] = useState<string | null>(null)
  const [surpriseIndex, setSurpriseIndex] = useState(0)
  const [imgSrc, setImgSrc] = useState(BIRTHDAY.photo)
  const [showEnding, setShowEnding] = useState(false)

  // Fire confetti once on load 🎉
  useEffect(() => {
    const t = setTimeout(() => setConfettiKey((k) => k + 1), 400)
    return () => clearTimeout(t)
  }, [])

  // Rotate funny messages
  useEffect(() => {
    const id = setInterval(() => {
      setMessageIndex((i) => (i + 1) % FUNNY_MESSAGES.length)
    }, 4200)
    return () => clearInterval(id)
  }, [])

  const handleSurprise = useCallback(() => {
    setConfettiKey((k) => k + 1)
    setSurprise(SURPRISES[surpriseIndex])
    setSurpriseIndex((i) => (i + 1) % SURPRISES.length)
  }, [surpriseIndex])

  return (
    <main className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-violet-500 via-pink-500 to-orange-400 text-white">
      {/* soft glow blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-yellow-300/30 blur-3xl" />
        <div className="absolute -right-24 top-1/3 h-80 w-80 rounded-full bg-sky-300/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-fuchsia-300/30 blur-3xl" />
      </div>

      <FloatingHearts />
      <ConfettiBurst fireKey={confettiKey} />

      <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center px-5 py-12 text-center sm:py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-2 text-5xl sm:text-6xl"
        >
          <motion.span
            animate={{ rotate: [0, -12, 12, -12, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 1.2 }}
            className="inline-block"
          >
            🎉
          </motion.span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 120, delay: 0.15 }}
          className="text-4xl font-extrabold leading-tight drop-shadow-lg sm:text-6xl"
        >
          Happy Birthday,
          <br />
          <span className="bg-gradient-to-r from-yellow-200 via-white to-yellow-200 bg-clip-text text-transparent">
            {BIRTHDAY.name}! 🥳
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-4 text-lg font-medium text-white/90 sm:text-xl"
        >
          The coolest boy in the universe just leveled up! 🚀
        </motion.p>

        {/* Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 100, delay: 0.3 }}
          className="mt-8"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-[2rem] bg-white/40 blur-xl" />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imgSrc}
              alt={`${BIRTHDAY.name} celebrating a birthday`}
              onError={() => setImgSrc(BIRTHDAY.photoFallback)}
              className="relative h-56 w-56 rounded-[2rem] border-4 border-white object-cover shadow-2xl sm:h-64 sm:w-64"
            />
            <span className="absolute -right-4 -top-4 text-4xl drop-shadow-lg">🎂</span>
            <span className="absolute -bottom-4 -left-4 text-4xl drop-shadow-lg">🎈</span>
          </motion.div>
        </motion.div>

        {/* Rotating funny messages */}
        <div className="mt-10 min-h-[7rem] w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={messageIndex}
              initial={{ opacity: 0, y: 16, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              exit={{ opacity: 0, y: -16, rotate: 1 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl border border-white/30 bg-white/15 px-6 py-5 text-lg font-semibold shadow-lg backdrop-blur-md sm:text-xl"
            >
              {FUNNY_MESSAGES[messageIndex]}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Surprise button */}
        <motion.button
          onClick={handleSurprise}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          className="mt-10 rounded-full bg-white px-9 py-4 text-lg font-extrabold text-pink-600 shadow-xl transition-shadow hover:shadow-2xl active:shadow-md sm:text-xl"
        >
          <motion.span
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="inline-flex items-center gap-2"
          >
            🎁 Press for a Surprise!
          </motion.span>
        </motion.button>

        {/* Surprise result */}
        <AnimatePresence>
          {surprise && (
            <motion.div
              key={surprise}
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="mt-6 max-w-md rounded-2xl bg-yellow-300 px-6 py-4 text-base font-bold text-neutral-800 shadow-lg sm:text-lg"
            >
              {surprise}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reveal heartfelt ending */}
        {!showEnding && (
          <motion.button
            onClick={() => {
              setShowEnding(true)
              setConfettiKey((k) => k + 1)
            }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="mt-12 rounded-full border-2 border-white/70 px-7 py-3 text-base font-semibold text-white/95 backdrop-blur-sm transition-colors hover:bg-white/15 sm:text-lg"
          >
            💌 Open a message from the heart
          </motion.button>
        )}

        <AnimatePresence>
          {showEnding && (
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mt-12 w-full rounded-[2rem] border border-white/30 bg-white/15 px-6 py-8 shadow-xl backdrop-blur-md sm:px-10 sm:py-10"
            >
              <div className="text-4xl">💛</div>
              <h2 className="mt-3 text-2xl font-extrabold sm:text-3xl">Dear Odin,</h2>
              <p className="mt-4 text-base leading-relaxed text-white/95 sm:text-lg">
                Beyond all the jokes, the confetti, and the ridiculous amount of cake we're
                about to eat — I just want you to know how genuinely grateful I am to have you
                in my life. You make ordinary days brighter, hard days lighter, and every
                adventure a whole lot more fun.
              </p>
              <p className="mt-4 text-base leading-relaxed text-white/95 sm:text-lg">
                You're kind, hilarious, and one of the best people I know. Here's to another
                year of laughing until it hurts, chasing big dreams, and making memories worth
                keeping. May this year bring you everything you wish for and a little extra.
              </p>
              <p className="mt-6 text-lg font-bold sm:text-xl">
                Happy Birthday, buddy. You deserve the whole world. 🌍✨
              </p>
              <p className="mt-2 text-base text-white/90">— With all my heart 💖</p>

              <motion.button
                onClick={() => setConfettiKey((k) => k + 1)}
                whileTap={{ scale: 0.94 }}
                className="mt-8 rounded-full bg-white px-7 py-3 text-base font-extrabold text-pink-600 shadow-lg sm:text-lg"
              >
                🎊 One more confetti blast!
              </motion.button>
            </motion.section>
          )}
        </AnimatePresence>

        <footer className="mt-14 text-sm text-white/70">
          Made with 💖 for the one and only {BIRTHDAY.name}
        </footer>
      </div>
    </main>
  )
}
